import { Box, Flex, Grid, GridItem, Spacer, Text } from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import { ValueDifferenceGridItem } from './BorrowingBaseSummaryValueDifferenceGridItem';
import {
  AllBorrowingBaseReportSummaryFields,
  BorrowingBaseSummaryColumnValueInput,
  borrowingBaseSummaryFormatterMap,
  BorrowingBaseSummaryValueType,
  getTemplateColumnsForAccordionRow,
} from './utils';
import { useLocalization } from 'packages/localization';
import { CalculationTerminologiesPopover } from '~/core/routes/calculation-terminologies/CalculationTerminologiesPopover';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';

type BorrowingBaseSummaryAccordionRowProps = {
  label: string;
  columnValues: BorrowingBaseSummaryColumnValueInput;
  valueType: BorrowingBaseSummaryValueType;
  hasAccordionPanel: boolean;
  isExpanded: boolean;
  showComparisonAndDifferenceColumns: boolean;
  showValueAndReferenceColumnsOnly: boolean;
  fieldName: AllBorrowingBaseReportSummaryFields;
};
export const BorrowingBaseSummaryAccordionRow = ({
  label,
  columnValues,
  valueType,
  hasAccordionPanel,
  isExpanded,
  showComparisonAndDifferenceColumns,
  showValueAndReferenceColumnsOnly,
  fieldName,
}: BorrowingBaseSummaryAccordionRowProps) => {
  const { getLabel } = useLocalization();
  const localizedLabel = getLabel(label);
  const formatter = borrowingBaseSummaryFormatterMap[valueType];

  const templateColumns = getTemplateColumnsForAccordionRow({
    showComparisonAndDifferenceColumns,
    showValueAndReferenceColumnsOnly,
  });

  // value difference calculation and formatting
  const minuend = columnValues.minuendValue;
  const subtrahend = columnValues.subtrahendValue;

  return (
    <Box padding="8px 16px" height="100%" width="100%">
      <Grid minH="48px" templateColumns={templateColumns} gridColumnGap="16px">
        <GridItem
          colSpan={showComparisonAndDifferenceColumns ? 1 : 2}
          alignSelf="center"
          fontSize="16px"
          fontWeight={600}
          textAlign="left"
          whiteSpace="pre-line"
          verticalAlign="text-bottom"
          display="flex"
          alignItems="center"
        >
          <Text>{localizedLabel}</Text>
          {label === localizedLabel && (
            <Box display="inline-block" ml="8px">
              <CalculationTerminologiesPopover termKeyLabel={label} />
            </Box>
          )}
        </GridItem>

        {/* If only a single column of values: */}
        {!showComparisonAndDifferenceColumns &&
          !showValueAndReferenceColumnsOnly && (
            <GridItem colSpan={1} alignSelf="center" />
          )}
        {!showComparisonAndDifferenceColumns &&
          !showValueAndReferenceColumnsOnly &&
          (_.isNumber(columnValues.minuendValue) ? (
            <GridItem
              colSpan={2}
              alignSelf="center"
              padding="8px 16px"
              backgroundColor={colors.aurora[50]}
            >
              <Flex>
                <Text fontSize="16px" fontWeight={600} textAlign="left">
                  {formatter(columnValues.minuendValue)}
                </Text>
                <Spacer />
                {hasAccordionPanel && (
                  <Icon
                    icon={
                      isExpanded ? 'circle-chevron-up' : 'circle-chevron-down'
                    }
                    size="large"
                    color={colors.aurora[400]}
                  />
                )}
              </Flex>
            </GridItem>
          ) : (
            <GridItem colSpan={2} alignSelf="center" padding="8px 16px">
              {hasAccordionPanel && (
                <Flex>
                  <Spacer />
                  <Icon
                    icon={
                      isExpanded ? 'circle-chevron-up' : 'circle-chevron-down'
                    }
                    size="large"
                    color={colors.aurora[400]}
                  />
                </Flex>
              )}
            </GridItem>
          ))}

        {/* If showing value and reference columns only: */}
        {showValueAndReferenceColumnsOnly &&
          (_.isNumber(columnValues.referenceValue) ? (
            <GridItem colSpan={1} alignSelf="center" padding="8px 16px">
              <Text
                fontSize="16px"
                fontWeight={400}
                textAlign="left"
                color={colors.black[70]}
              >
                {formatter(columnValues.referenceValue)}
              </Text>
            </GridItem>
          ) : (
            <GridItem colSpan={1} alignSelf="center" />
          ))}
        {showValueAndReferenceColumnsOnly &&
          (_.isNumber(columnValues.minuendValue) ? (
            <GridItem
              colSpan={1}
              alignSelf="center"
              padding="8px 16px"
              backgroundColor={colors.aurora[50]}
            >
              <Flex>
                <Text fontSize="16px" fontWeight={600} textAlign="left">
                  {formatter(columnValues.minuendValue)}
                </Text>
                <Spacer />
                {hasAccordionPanel && (
                  <Icon
                    icon={
                      isExpanded ? 'circle-chevron-up' : 'circle-chevron-down'
                    }
                    size="large"
                    color={colors.aurora[400]}
                  />
                )}
              </Flex>
            </GridItem>
          ) : (
            <GridItem colSpan={1} alignSelf="center" padding="8px 16px">
              {hasAccordionPanel && (
                <Flex>
                  <Spacer />
                  <Icon
                    icon={
                      isExpanded ? 'circle-chevron-up' : 'circle-chevron-down'
                    }
                    size="large"
                    color={colors.aurora[400]}
                  />
                </Flex>
              )}
            </GridItem>
          ))}

        {/* If showing all columns of comparison and difference values: */}
        {showComparisonAndDifferenceColumns && (
          <GridItem colSpan={1} alignSelf="center" padding="8px 16px">
            <Text
              fontSize="16px"
              fontWeight={400}
              textAlign="left"
              color={colors.black[70]}
            >
              {formatter(columnValues.referenceValue)}
            </Text>
          </GridItem>
        )}
        {showComparisonAndDifferenceColumns && (
          <GridItem colSpan={1} alignSelf="center" padding="8px 16px">
            <Text fontSize="16px" fontWeight={600} textAlign="left">
              {formatter(columnValues.subtrahendValue)}
            </Text>
          </GridItem>
        )}
        {showComparisonAndDifferenceColumns && (
          <GridItem colSpan={1} alignSelf="center" padding="8px 16px">
            <Text fontSize="16px" fontWeight={600} textAlign="left">
              {formatter(columnValues.minuendValue)}
            </Text>
          </GridItem>
        )}
        {showComparisonAndDifferenceColumns && (
          // Difference column with special formatting
          <ValueDifferenceGridItem
            minuend={minuend}
            subtrahend={subtrahend}
            valueType={valueType}
            fieldName={fieldName}
          />
        )}
        {showComparisonAndDifferenceColumns && hasAccordionPanel && (
          // Accordion Arrow Icon
          <Flex alignItems="center">
            <Icon
              icon={isExpanded ? 'circle-chevron-up' : 'circle-chevron-down'}
              size="large"
              color={colors.aurora[400]}
            />
          </Flex>
        )}
      </Grid>
    </Box>
  );
};
